import api from './client'

export interface AnswerChoice {
  id: number
  choice_label: string
  choice_text: string
  is_correct: boolean
  explanation: string | null
  display_order: number
}

export interface QuestionAnswer {
  id: number
  question_set_id: number
  question: string
  question_image: string | null
  display_order: number
  source_question_answer_id: number | null
  explanation: string | null
  question_type: string  // "multiple_choice" | "spr"
  correct_answer_text: string | null
  answer_tolerance: number | null
  choices: AnswerChoice[]
  create_datetime: string | null
}

export interface QuestionSet {
  id: number
  mastery_id: number
  source: string | null
  module: string | null
  sequence_in_source: number | null
  question_text: string | null
  question_images: string[]
  difficulty_level: number
  ai_flag: boolean
  verified_flag: boolean
  explanation: string | null
  created_by: number | null
  question_answers: QuestionAnswer[]
  create_datetime: string | null
}

// Legacy alias used by existing QuestionManager code
export interface Question {
  id: number
  mastery_id: number
  question_text: string
  question_images: string[]
  difficulty_level: number
  ai_flag: boolean
  verified_flag: boolean
  source_question_id: number | null
  explanation: string | null
  created_by: number | null
  choices: AnswerChoice[]
  create_datetime: string | null
}

export interface ChoiceInput {
  choice_label: string
  choice_text: string
  is_correct: boolean
  explanation: string
  display_order: number
}

export interface QuestionAnswerInput {
  question: string
  question_image?: string | null
  display_order: number
  explanation?: string | null
  question_type: string
  correct_answer_text?: string | null
  answer_tolerance?: number | null
  choices: ChoiceInput[]
}

export interface QuestionCreate {
  mastery_id: number
  question_text: string
  difficulty_level: number
  explanation: string
  choices: ChoiceInput[]
}

export interface QuestionSetUpdatePayload {
  source?: string | null
  module?: string | null
  sequence_in_source?: number | null
  question_text?: string | null
  question_images?: string[]
  difficulty_level?: number
  explanation?: string | null
  verified_flag?: boolean
  question_answers?: QuestionAnswerInput[]
}

export interface PaginatedResponse {
  items: QuestionSet[]
  total: number
}

export const questionsApi = {
  list: (masteryId: number) =>
    api.get<Question[]>(`/questions?mastery_id=${masteryId}&verified_only=false`),
  create: (data: QuestionCreate) => api.post<Question>('/questions', data),
  update: (id: number, data: Partial<QuestionCreate>) =>
    api.put<Question>(`/questions/${id}`, data),
  delete: (id: number) => api.delete(`/questions/${id}`),
  verify: (id: number) => api.put(`/questions/${id}/verify`),
}

export interface AdminQuestionFilters {
  search?: string
  domain_id?: number
  mastery_id?: number
  portion_id?: number
  source?: string
  question_set_id?: number
  skip?: number
  limit?: number
}

export const adminQuestionsApi = {
  list: (filters: AdminQuestionFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.search) params.set('search', filters.search)
    if (filters.domain_id) params.set('domain_id', String(filters.domain_id))
    if (filters.mastery_id) params.set('mastery_id', String(filters.mastery_id))
    if (filters.portion_id) params.set('portion_id', String(filters.portion_id))
    if (filters.source) params.set('source', filters.source)
    if (filters.question_set_id != null) params.set('question_set_id', String(filters.question_set_id))
    params.set('skip', String(filters.skip ?? 0))
    params.set('limit', String(filters.limit ?? 20))
    return api.get<PaginatedResponse>(`/admin/questions?${params.toString()}`)
  },
  listSources: () => api.get<string[]>('/admin/questions/sources'),
  get: (id: number) => api.get<QuestionSet>(`/questions/${id}`),
  update: (id: number, data: QuestionSetUpdatePayload) =>
    api.put(`/admin/questions/${id}`, data),
  delete: (id: number) => api.delete(`/admin/questions/${id}`),
  verify: (id: number) => api.put(`/admin/questions/${id}/verify`),
}
